import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Users, CreditCard, BookOpen, AlertTriangle, Scale } from 'lucide-react';

const TermsOfService = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: "Terms of Service",
      lastUpdated: "Last Updated: February 21, 2026",
      backHome: "Back to Home",
      sections: [
        {
          icon: FileText,
          title: "Agreement to Terms",
          content: `By accessing and using the Canada English Life Academy website and services, you agree to be bound by these Terms of Service. If you do not agree with any part of these terms, you may not access our services.

These terms apply to all visitors, users, students, and others who access or use our services.`
        },
        {
          icon: Users,
          title: "User Accounts",
          content: `When you create an account with us, you must provide accurate and complete information. You are responsible for:
• Maintaining the confidentiality of your account credentials
• All activities that occur under your account
• Notifying us immediately of any unauthorized access

We reserve the right to suspend or terminate accounts that violate these terms or engage in fraudulent activity.`
        },
        {
          icon: BookOpen,
          title: "Course Enrollment & Access",
          content: `Upon enrollment and payment:
• You will receive access to course materials for the duration specified
• Course content is for personal, non-commercial use only
• You may not share, distribute, or reproduce course materials
• Access may be revoked for violation of these terms

Course schedules and content may be modified at our discretion with reasonable notice to enrolled students.`
        },
        {
          icon: CreditCard,
          title: "Payments & Billing",
          content: `All payments are processed securely through our payment providers:
• Prices are displayed in the currency indicated at checkout
• Payment is required before course access is granted
• We accept major credit cards and other payment methods as displayed
• All fees are non-refundable unless otherwise stated in our Refund Policy

You agree to provide current, complete, and accurate billing information.`
        },
        {
          icon: AlertTriangle,
          title: "Prohibited Activities",
          content: `You agree not to:
• Use our services for any illegal purpose
• Share your account credentials with others
• Copy, distribute, or modify course content without permission
• Interfere with or disrupt our services
• Attempt to gain unauthorized access to our systems
• Harass, abuse, or harm other users or instructors
• Submit false information or impersonate others

Violation of these rules may result in immediate termination of your account.`
        },
        {
          icon: Scale,
          title: "Limitation of Liability",
          content: `Canada English Life Academy provides educational services on an "as is" basis. We do not guarantee:
• Specific learning outcomes or results
• Uninterrupted access to services
• That services will meet all your requirements

To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our services.

For questions about these Terms, contact us at:
Email: info@canada-englishlife.com
Phone: +1 647 874 5620`
        }
      ]
    },
    ar: {
      title: "شروط الخدمة",
      lastUpdated: "آخر تحديث: 21 فبراير 2026",
      backHome: "العودة للرئيسية",
      sections: [
        {
          icon: FileText,
          title: "الموافقة على الشروط",
          content: `من خلال الوصول إلى موقع وخدمات أكاديمية Canada English Life واستخدامها، فإنك توافق على الالتزام بشروط الخدمة هذه. إذا كنت لا توافق على أي جزء من هذه الشروط، فلا يجوز لك الوصول إلى خدماتنا.

تنطبق هذه الشروط على جميع الزوار والمستخدمين والطلاب وغيرهم ممن يصلون إلى خدماتنا أو يستخدمونها.`
        },
        {
          icon: Users,
          title: "حسابات المستخدمين",
          content: `عند إنشاء حساب معنا، يجب عليك تقديم معلومات دقيقة وكاملة. أنت مسؤول عن:
• الحفاظ على سرية بيانات اعتماد حسابك
• جميع الأنشطة التي تحدث تحت حسابك
• إخطارنا فوراً بأي وصول غير مصرح به

نحتفظ بالحق في تعليق أو إنهاء الحسابات التي تنتهك هذه الشروط أو تشارك في نشاط احتيالي.`
        },
        {
          icon: BookOpen,
          title: "التسجيل في الدورات والوصول",
          content: `عند التسجيل والدفع:
• ستحصل على وصول إلى مواد الدورة للمدة المحددة
• محتوى الدورة للاستخدام الشخصي غير التجاري فقط
• لا يجوز لك مشاركة أو توزيع أو إعادة إنتاج مواد الدورة
• يمكن إلغاء الوصول بسبب انتهاك هذه الشروط

قد يتم تعديل جداول الدورات ومحتواها وفقاً لتقديرنا مع إشعار معقول للطلاب المسجلين.`
        },
        {
          icon: CreditCard,
          title: "المدفوعات والفواتير",
          content: `تتم معالجة جميع المدفوعات بشكل آمن من خلال مزودي الدفع لدينا:
• يتم عرض الأسعار بالعملة المشار إليها عند الدفع
• الدفع مطلوب قبل منح الوصول إلى الدورة
• نقبل بطاقات الائتمان الرئيسية وطرق الدفع الأخرى كما هو معروض
• جميع الرسوم غير قابلة للاسترداد ما لم ينص على خلاف ذلك في سياسة الاسترداد الخاصة بنا

أنت توافق على تقديم معلومات فواتير حالية وكاملة ودقيقة.`
        },
        {
          icon: AlertTriangle,
          title: "الأنشطة المحظورة",
          content: `أنت توافق على عدم:
• استخدام خدماتنا لأي غرض غير قانوني
• مشاركة بيانات اعتماد حسابك مع الآخرين
• نسخ أو توزيع أو تعديل محتوى الدورة دون إذن
• التدخل في خدماتنا أو تعطيلها
• محاولة الوصول غير المصرح به إلى أنظمتنا
• مضايقة أو إساءة أو إيذاء المستخدمين أو المدربين الآخرين
• تقديم معلومات كاذبة أو انتحال شخصية الآخرين

قد يؤدي انتهاك هذه القواعد إلى إنهاء حسابك فوراً.`
        },
        {
          icon: Scale,
          title: "حدود المسؤولية",
          content: `تقدم أكاديمية Canada English Life خدمات تعليمية "كما هي". نحن لا نضمن:
• نتائج تعليمية أو نتائج محددة
• الوصول دون انقطاع إلى الخدمات
• أن الخدمات ستلبي جميع متطلباتك

إلى أقصى حد يسمح به القانون، لن نكون مسؤولين عن أي أضرار غير مباشرة أو عرضية أو خاصة أو تبعية ناشئة عن استخدامك لخدماتنا.

للأسئلة حول هذه الشروط، اتصل بنا على:
البريد الإلكتروني: info@canada-englishlife.com
الهاتف: +1 647 874 5620`
        }
      ]
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50/30" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_english-academy-lms/artifacts/fhrsr91w_profile_picture.png"
                alt="Canada English Life Academy"
                className="h-10 w-auto object-contain"
              />
              <span className="text-lg font-bold text-gray-900">Canada English Life</span>
            </Link>
            <Link 
              to="/" 
              className="flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium transition-colors"
            >
              <ArrowLeft className={`w-4 h-4 ${language === 'ar' ? 'rotate-180' : ''}`} />
              {t.backHome}
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Title */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-teal-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{t.title}</h1>
          <p className="text-gray-500">{t.lastUpdated}</p>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {t.sections.map((section, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
            >
              <div className="p-6 md:p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <section.icon className="w-5 h-5 text-teal-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 pt-1">{section.title}</h2>
                </div>
                <div className="text-gray-600 leading-relaxed whitespace-pre-line">
                  {section.content}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer Note */}
        <div className="mt-12 text-center text-gray-500 text-sm">
          <p>© 2026 Canada English Life Academy. All rights reserved.</p>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;
